import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { CalendarEventLimit } from "@tellescope/types-models";
import { Button, Label, MultiSelect, MultiSelectOption, NumberInput } from "@tellescope/ui";
import { Trash2 } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface LimitBlockProps {
  limit: CalendarEventLimit;
  onChange: (limit: CalendarEventLimit) => void;
  onDelete: () => void;
  canUpdate: boolean;
}

export const LimitBlock = ({ limit, onChange, onDelete, canUpdate }: LimitBlockProps) => {
  const { t } = useTranslation("calendar");
  const allTemplateIds = useMemo(() => {
    const set = new Set(limit.otherTemplateIds ?? []);
    set.add(limit.templateId);
    return Array.from(set);
  }, [limit.otherTemplateIds, limit.templateId]);
  const allTemplatesOptionsQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) =>
      data.pages.flat().reduce<MultiSelectOption[]>((acc, curr) => {
        acc.push({
          label: curr.displayTitle || curr.title,
          value: curr.id,
        });
        return acc;
      }, []),
  });

  return (
    <div className="border rounded-sm p-2 flex gap-2 items-center">
      <MultiSelect
        value={allTemplateIds}
        options={allTemplatesOptionsQuery.data ?? []}
        onValueChange={(templateIds) => {
          const shouldUpdateMainId = limit.templateId && !templateIds.includes(limit.templateId);
          const [mainId, ...rest] = templateIds;
          if (shouldUpdateMainId && mainId) {
            onChange({
              ...limit,
              templateId: mainId,
              otherTemplateIds: rest,
            });
          } else {
            onChange({
              ...limit,
              otherTemplateIds: templateIds.filter((id) => id !== limit.templateId),
            });
          }
        }}
        className="w-auto grow"
        disabled={!canUpdate}
      />
      <span className="flex gap-2">
        <Label className="text-xs text-muted-foreground font-light">{t("max")}</Label>
        <NumberInput
          min={1}
          value={limit.limit}
          onChange={(newLimit) =>
            onChange({
              ...limit,
              limit: newLimit ?? limit.limit,
            })
          }
          className="w-[75px] text-xs font-light"
          disabled={!canUpdate}
        />
      </span>
      <span className="flex gap-2">
        <Label className="text-xs text-muted-foreground font-light">{t("per")}</Label>
        <NumberInput
          min={1}
          value={limit.period}
          onChange={(period) =>
            onChange({
              ...limit,
              period: period ?? limit.period,
            })
          }
          className="w-[75px] text-xs font-light"
          disabled={!canUpdate}
        />
        <Label className="text-xs text-muted-foreground font-light">{t("days")}</Label>
      </span>
      <Button variant="ghost" size="sm-icon" onClick={onDelete} className="text-muted-foreground" disabled={!canUpdate}>
        <Trash2 className="size-3.5" />
      </Button>
    </div>
  );
};
